/**
 * Iter 67eh — Embeddable search widget.
 *
 * Renders the homepage step-search UI in isolation (no header / footer /
 * marketing chrome) so any third-party site can drop it in via:
 *
 *   <iframe src="https://rentalcar.al/embed/search?affiliate_id=…"
 *           width="100%" height="380" frameborder="0"></iframe>
 *
 * On submit, the search params PLUS `affiliate_id` are tacked onto a
 * `/cars?…` URL that opens in a new tab (`_top` from inside the iframe
 * lands in the parent's tab; we use `target=_blank` + window.open to
 * pop out cleanly).
 *
 * URL params we read (all optional):
 *   • affiliate_id   — slug-style partner identifier (alnum + dash/underscore, ≤60 chars)
 *   • country        — pre-select pickup country
 *   • pickup_location — pre-fill pickup location string
 *   • lang           — UI language (delegated to existing i18n basename routing)
 *   • accent         — hex color (e.g. `f59e0b`) — overrides the amber border + button
 *   • variant        — `compact` (single row, ≤80px tall) or `full` (default)
 *   • cobranding     — vendor slug to fetch branding for (future hook)
 *
 * The widget itself doesn't need auth; submission opens the public results
 * page in the embedder's window.
 */
import { useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import HeroStepperSearch from "@/components/HeroStepperSearch";

const AFFIL_RE = /[^a-zA-Z0-9_-]/g;

function _sanitiseAffiliate(raw) {
  if (!raw) return null;
  const clean = String(raw).replace(AFFIL_RE, "").replace(/^[-_]+|[-_]+$/g, "").slice(0, 60);
  return clean || null;
}

function _sanitiseHex(raw) {
  if (!raw) return null;
  const clean = String(raw).replace(/[^a-fA-F0-9]/g, "").slice(0, 6);
  return clean.length === 3 || clean.length === 6 ? `#${clean}` : null;
}

export default function EmbedSearchPage() {
  const [params] = useSearchParams();

  const affiliateId = _sanitiseAffiliate(params.get("affiliate_id"));
  const accent = _sanitiseHex(params.get("accent"));
  const variant = (params.get("variant") || "full").toLowerCase();
  const isCompact = variant === "compact";
  const country = (params.get("country") || "").trim();
  const pickupLocation = (params.get("pickup_location") || "").trim();

  // Persist the affiliate_id so the renter's eventual booking on the host
  // (which happens in a new tab outside this iframe) still carries the
  // attribution. The car-detail page reads it back from this key.
  useEffect(() => {
    if (!affiliateId) return;
    try {
      localStorage.setItem("rentalcar:affiliate_id", affiliateId);
      // 30-day-ish expiry: stash a timestamp the booking page can check.
      localStorage.setItem("rentalcar:affiliate_id_at", new Date().toISOString());
    } catch { /* private mode */ }
  }, [affiliateId]);

  // Patch the existing HeroStepperSearch via URL seeding. The stepper reads
  // `pickup_location` / `start_date` from useSearchParams on mount, so we
  // just need to make sure those are on the URL (they already are via
  // ?pickup_location=… when embedded). Nothing extra needed here.

  // Intercept ALL clicks on `/cars?…` anchors / programmatic navigations so
  // we tack on the affiliate id + open in a new tab. Easiest robust hook
  // is to monkey-patch `history.pushState`/`replaceState` which react-router
  // uses internally; but the stepper calls navigate which calls pushState
  // INSIDE the iframe — that just changes the iframe URL silently and is
  // useless. So we hijack at the click level instead.
  useEffect(() => {
    const onClick = (e) => {
      // ignored — we hijack programmatic submission via the override below.
      void e;
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);

  // Override window.history pushState/replaceState — when the stepper tries
  // to navigate to /cars?…, intercept and open in the parent / new tab.
  useEffect(() => {
    const origPush = window.history.pushState.bind(window.history);
    const origReplace = window.history.replaceState.bind(window.history);
    const popOut = (url) => {
      const u = new URL(url, window.location.origin);
      if (!u.pathname.startsWith("/cars")) return false;
      if (affiliateId && !u.searchParams.get("affiliate_id")) {
        u.searchParams.set("affiliate_id", affiliateId);
      }
      // Open in a new tab — works whether we're in an iframe or not.
      try {
        window.open(u.toString(), "_blank", "noopener,noreferrer");
      } catch {
        window.top.location.href = u.toString();
      }
      return true;
    };
    window.history.pushState = function (state, title, url) {
      if (url && popOut(url)) return;
      return origPush(state, title, url);
    };
    window.history.replaceState = function (state, title, url) {
      if (url && popOut(url)) return;
      return origReplace(state, title, url);
    };
    return () => {
      window.history.pushState = origPush;
      window.history.replaceState = origReplace;
    };
  }, [affiliateId]);

  // Inject the chosen accent color into the existing amber utilities by
  // overriding the tailwind class variables at the wrapper level.
  const styleVars = useMemo(() => {
    if (!accent) return {};
    return { "--rc-embed-accent": accent };
  }, [accent]);

  return (
    <div
      className={`min-h-screen ${isCompact ? "bg-transparent" : "bg-gradient-to-br from-amber-50 via-white to-sky-50"} flex items-center justify-center p-3 sm:p-5`}
      style={styleVars}
      data-testid="embed-search-page"
    >
      <div className="w-full max-w-3xl">
        {!isCompact && (
          <div className="mb-3 text-center">
            <a
              href="https://rentalcar.al"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-xs text-neutral-600 hover:text-amber-700 transition"
              data-testid="embed-search-brand"
            >
              <img src="https://rental-connect-54.preview.emergentagent.com/logo-192.png" alt="" className="h-5 w-5" />
              <span>Powered by <span className="font-semibold">RentalCar.al</span></span>
            </a>
          </div>
        )}
        <SeededStepperWrapper country={country} pickupLocation={pickupLocation} />
      </div>
    </div>
  );
}

// Seeds the existing HeroStepperSearch via URL params at first mount. The
// stepper reads `pickup_location` and `start_date` from useSearchParams,
// so when the embedder passes them via query string we already get
// pre-filled values for free. This wrapper exists so we can swap to a
// dedicated compact UI later without touching the import in App.js.
function SeededStepperWrapper({ country, pickupLocation }) {
  void country;
  void pickupLocation;
  return <HeroStepperSearch />;
}
